import {
  EmailIngestionFormValues,
  FormErrors,
  SftpIngestionFormValues,
} from "../types";

// Limits come from the Config database columns (see IngestionConstants in the
// backend). Encrypted columns store Base64(AES(value)), which is longer than
// the value, so those limits are in UTF-8 bytes of the plain text.
export const INGESTION_LIMITS = {
  description: 500,
  sourceDirectory: 2000,
  emailFolder: 100,
  mask: 100,
  mailBoxDescription: 200,
  hostNameBytes: 735,
  startDirectoryBytes: 367,
  ipAddressBytes: 31,
  credentialBytes: 63,
} as const;

export const EMPTY_SFTP_FORM: SftpIngestionFormValues = {
  description: "",
  enabled: true,
  sourceDirectory: "",
  startDirectory: "",
  transferSubDirectory: false,
  upload: false,
  hostName: "",
  ipAddress: "",
  portNumber: "",
  userName: "",
  password: "",
  hostKeyFingerPrint: "",
};

export const EMPTY_EMAIL_FORM: EmailIngestionFormValues = {
  description: "",
  enabled: true,
  folder: "",
  senderMask: "*",
  attachmentMask: "*.*",
  mailBoxDescription: "",
  destinationFileShareId: null,
  destinationSftpId: null,
  address: "",
  password: "",
};

export const utf8ByteLength = (value: string): number =>
  new TextEncoder().encode(value).length;

const requiredWithMax = (
  value: string,
  label: string,
  max: number,
): string | undefined => {
  if (!value.trim()) {
    return `${label} is required`;
  }
  return value.length > max
    ? `${label} must be at most ${max} characters`
    : undefined;
};

const optionalMax = (
  value: string,
  label: string,
  max: number,
): string | undefined =>
  value.length > max ? `${label} must be at most ${max} characters` : undefined;

const maxBytes = (
  value: string,
  label: string,
  max: number,
): string | undefined =>
  utf8ByteLength(value) > max
    ? `${label} is too long (at most ${max} bytes)`
    : undefined;

const validPort = (value: string): string | undefined => {
  if (!value.trim()) {
    return undefined;
  }
  const port = Number(value);
  return Number.isInteger(port) && port >= 1 && port <= 65535
    ? undefined
    : "Port must be a whole number between 1 and 65535";
};

// Drops fields without an error so a valid form returns {}.
const compact = <T>(
  errors: Record<keyof T, string | undefined>,
): FormErrors<T> =>
  Object.fromEntries(
    Object.entries(errors).filter(([, message]) => message !== undefined),
  ) as FormErrors<T>;

/** Connection fields are optional: blank means "copy" (create) or "keep" (edit). */
export const validateSftpIngestion = (
  values: SftpIngestionFormValues,
): FormErrors<SftpIngestionFormValues> =>
  compact<SftpIngestionFormValues>({
    description: requiredWithMax(
      values.description,
      "Description",
      INGESTION_LIMITS.description,
    ),
    enabled: undefined,
    sourceDirectory: requiredWithMax(
      values.sourceDirectory,
      "Source directory",
      INGESTION_LIMITS.sourceDirectory,
    ),
    startDirectory: maxBytes(
      values.startDirectory,
      "Start directory",
      INGESTION_LIMITS.startDirectoryBytes,
    ),
    transferSubDirectory: undefined,
    upload: undefined,
    hostName: maxBytes(
      values.hostName,
      "Host name",
      INGESTION_LIMITS.hostNameBytes,
    ),
    ipAddress: maxBytes(
      values.ipAddress,
      "IP address",
      INGESTION_LIMITS.ipAddressBytes,
    ),
    portNumber: validPort(values.portNumber),
    userName: maxBytes(
      values.userName,
      "User name",
      INGESTION_LIMITS.credentialBytes,
    ),
    password: maxBytes(
      values.password,
      "Password",
      INGESTION_LIMITS.credentialBytes,
    ),
    hostKeyFingerPrint: maxBytes(
      values.hostKeyFingerPrint,
      "Host key fingerprint",
      INGESTION_LIMITS.credentialBytes,
    ),
  });

export const validateEmailIngestion = (
  values: EmailIngestionFormValues,
): FormErrors<EmailIngestionFormValues> =>
  compact<EmailIngestionFormValues>({
    description: requiredWithMax(
      values.description,
      "Description",
      INGESTION_LIMITS.description,
    ),
    enabled: undefined,
    folder: requiredWithMax(
      values.folder,
      "Mailbox folder",
      INGESTION_LIMITS.emailFolder,
    ),
    senderMask: requiredWithMax(
      values.senderMask,
      "Sender mask",
      INGESTION_LIMITS.mask,
    ),
    attachmentMask: requiredWithMax(
      values.attachmentMask,
      "Attachment mask",
      INGESTION_LIMITS.mask,
    ),
    mailBoxDescription: optionalMax(
      values.mailBoxDescription,
      "Mailbox description",
      INGESTION_LIMITS.mailBoxDescription,
    ),
    destinationFileShareId: undefined,
    destinationSftpId: undefined,
    address: maxBytes(
      values.address,
      "Mailbox address",
      INGESTION_LIMITS.credentialBytes,
    ),
    password: maxBytes(
      values.password,
      "Password",
      INGESTION_LIMITS.credentialBytes,
    ),
  });
